import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { isAlgoliaEnabled, searchAndRender, fetchAndRenderEmptyState } from '@theme/apc-algolia-predictive-search';
import { publishToShopify } from '@theme/apc-datalayer';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [commandLabel] - The keyboard shortcut command label element.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.addEventListener('click', this.#handleModalClick, { signal });

    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });

    if (dialog) {
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }

    // Update keyboard shortcut label based on platform
    this.#updateCommandLabel();

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Updates the keyboard shortcut label based on the user's platform
   */
  #updateCommandLabel() {
    const { commandLabel } = this.refs;
    if (!commandLabel) return;

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    commandLabel.textContent = isMac ? '⌘ + K' : 'Ctrl + K';
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Check if click is on a query suggestion
    const querySuggestion = target.closest('.predictive-search-results__card--query');
    if (querySuggestion) {
      // When the suggestion is a real link (Algolia flyout), let it navigate to
      // the search page instead of just populating the input.
      if (querySuggestion.querySelector('a[href]')) {
        return;
      }
      event.preventDefault();
      this.#handleQuerySuggestionSelect(querySuggestion);
      return;
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles query suggestion selection by populating the input field
   * @param {HTMLElement} querySuggestion - The query suggestion element
   */
  #handleQuerySuggestionSelect(querySuggestion) {
    // Support both <a> (search results) and <span> (empty state) pill elements
    const pillElement = querySuggestion.querySelector('.pills__pill, .flyout-suggestion');
    if (!pillElement) return;

    const ariaLabel = pillElement.querySelector('[aria-label]')?.getAttribute('aria-label');
    const suggestionText = ariaLabel || pillElement.textContent?.trim() || '';

    if (suggestionText) {
      this.refs.searchInput.value = suggestionText;
      this.#showResetButton();
      this.#getSearchResults(suggestionText);
      this.refs.searchInput.focus();
    }
  }

  /**
   * Handles the CMD+K (Mac) or CTRL+K (Windows/Linux) key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      if (this.dialog) {
        this.dialog.toggleDialog();
      } else {
        this.refs.searchInput.focus();
      }
    }
  };

  /**
   * Handles the dialog open event — auto-focus the search input.
   */
  #handleDialogOpen = () => {
    requestAnimationFrame(() => {
      this.refs.searchInput.focus();
    });
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__wrapper-collections, ' +
          '.predictive-search-results__list, ' +
          '.flyout__list',
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * Last query pushed as a `search_interaction` event, used to de-duplicate
   * consecutive identical searches.
   * @type {string}
   */
  #lastTrackedSearchTerm = '';

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();

          // Check if the current item is a query suggestion
          const currentItem = this.#currentItem;
          const suggestionLink = currentItem?.classList.contains('predictive-search-results__card--query')
            ? currentItem.querySelector('a[href]')
            : null;
          if (currentItem?.classList.contains('predictive-search-results__card--query') && !suggestionLink) {
            // Span-based pill with no link: populate the input instead.
            this.#handleQuerySuggestionSelect(currentItem);
          } else {
            // For links (suggestions, products, collections, etc.), follow the link
            currentItem?.querySelector('a')?.click();
          }
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);

    if (searchTerm !== this.#lastTrackedSearchTerm) {
      this.#lastTrackedSearchTerm = searchTerm;
      publishToShopify('search_interaction', { query: searchTerm });
    }
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /** @returns {Record<string, string | undefined>} */
  get #algoliaTranslations() {
    const ds = this.dataset;
    const helpTemplate = this.querySelector('template[ref="helpLinksTemplate"]');
    const brandsTemplate = this.querySelector('template[ref="brandsTemplate"]');
    const featuredCollectionsTemplate = this.querySelector('template[ref="featuredCollectionsTemplate"]');
    return {
      tSuggestions: ds.tSuggestions || undefined,
      tProducts: ds.tProducts || undefined,
      tProductsFor: ds.tProductsFor || undefined,
      tMoreProducts: ds.tMoreProducts || undefined,
      tCategories: ds.tCategories || undefined,
      tNoResults: ds.tNoResults || undefined,
      tBestSelling: ds.tBestSelling || undefined,
      helpLinks: helpTemplate ? helpTemplate.innerHTML : undefined,
      brandsHtml: brandsTemplate ? brandsTemplate.innerHTML : undefined,
      featuredCollectionsHtml: featuredCollectionsTemplate ? featuredCollectionsTemplate.innerHTML : undefined,
    };
  }

  /**
   * Fetch search results and update the results container.
   * Uses Algolia when enabled, falls back to Shopify's predictive search API.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    if (isAlgoliaEnabled()) {
      searchAndRender(searchTerm, abortController.signal, this.#algoliaTranslations)
        .then((resultsMarkup) => {
          if (abortController.signal.aborted) return;
          morph(predictiveSearchResults, resultsMarkup);
          this.#resetScrollPositions();
        })
        .catch((error) => {
          if (abortController.signal.aborted) return;
          if (error.name === 'AbortError') return;
          console.error('Algolia search failed, falling back to Shopify search:', error);
          this.#getShopifySearchResults(searchTerm, abortController);
        });
    } else {
      this.#getShopifySearchResults(searchTerm, abortController);
    }
  }

  /**
   * Fetch search results using Shopify's section renderer (original implementation).
   * @param {string} searchTerm - The term to search for
   * @param {AbortController} abortController - The abort controller for this request
   */
  async #getShopifySearchResults(searchTerm, abortController) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();

    // When Algolia is enabled, render the 3-column flyout empty state
    if (isAlgoliaEnabled()) {
      try {
        const emptyHtml = await fetchAndRenderEmptyState(abortController.signal, this.#algoliaTranslations);
        if (abortController.signal.aborted) return;
        morph(predictiveSearchResults, emptyHtml);
        this.#resetScrollPositions();
      } catch (error) {
        if (abortController.signal.aborted) return;
        if (error.name === 'AbortError') return;
        console.error('Algolia empty state failed, falling back to Shopify:', error);
        this.#resetSearchShopify(abortController);
      }
      return;
    }

    this.#resetSearchShopify(abortController);
  };

  /** Shopify-powered empty state (fallback). */
  #resetSearchShopify = async (/** @type {AbortController} */ abortController) => {
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = /** @type {HTMLElement | null} */ (
      new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section')
    );

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
